import { DateService, DateUnit } from "@app/services/date.service";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { DateRange } from "@enums/DateRange.enum";
import { IVehicle } from "@interfaces/IVehicle";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { DriverAssignmentService } from "@services/driverAssignment.service";
import { GeocodeService } from "@services/geocode.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import { VideoEntryService } from "../../videos/services/video-entry.service";
import "./videoDiagnostics.component.scss";
import { VideoDiagnosticsService } from './videoDiagnostics.service';

enum AlarmType {
    VideoLoss = 1,
    MotionDetection = 2,
    Cover = 3,
    StorageException = 4,
    IO1 = 5,
    IO2 = 6,
    IO3 = 7,
    IO4 = 8,
    IO5 = 9,
    IO6 = 10,
    IO7 = 11,
    IO8 = 12,
    PanicAlarm = 13,
    LowSpeed = 14,
    HighSpeed = 15,
    LowVoltage = 16,
    ACC = 17,
    Fence = 18,
    IllegalPowerOff = 19,
    IllegalShutdown = 20,
    TemperatureAlarm = 29,
    DistanceAlarm = 36,
    TemperatureChangeAlarm = 47,
    FatigueDriving = 58,
    NoDriver = 59,
    DriverCall = 60,
    DriverSmoke = 61,
    DriverUnderDistraction = 62,
    LaneDeparture = 63,
    FontCarCollision = 64,
    AbnormalBootAlarm = 74,
    CarDistanceTooClose = 162,
    PedestrianCollision = 163,
    DriverYawn = 164,
    LeftBlindZone = 165,
    RightBlindZone = 166,
}


class ComponentController implements angular.IComponentController {
    currentTabIndex: number = 0;

    vehicles: IVehicle[];
    selectedVehicle: IVehicle = null;
    alarmData: any[];

    loading: boolean = false;
    alarmsLoading: boolean = false;

    tableHeaders: IVeriTableHeader[];
    tableRows: IVeriTableRow<any>[] = [];

    defaultDateRange: DateRange = DateRange.Today;
    dateFilter: IDateFilter;

    videoSourceUrl: string;
    tempVideoSourceUrl: string;

    playbackSourceUrl: string;
    tempPlaybackSourceUrl: string;

    ignoredCodes: string[] = ["sensor", "left", "right", "reverse", "rear"];

    static $inject = [ "driverAssignmentService", "geocodeService", "translationService", "veriDate", "veriTableService", "videoDiagnosticsService", "dateService", "videoEntryService"];

    constructor(
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly geocodeService: GeocodeService,
        private readonly translationService: TranslationService,
        private readonly veriDate: VeriDate,
        private readonly veriTableService: VeriTableService,
        private readonly videoDiagnosticsService: VideoDiagnosticsService,
        private readonly dateService: DateService,
        private readonly videoEntryService: VideoEntryService) {
    }

    $onInit() {
        this.loadData();
    }

    $onChanges() {
        this.dateFilter = this.veriDate.dateRangeFromEnum(this.defaultDateRange);
    }

    private loadData() {
        this.loading = true;
        this.driverAssignmentService
            .getVehiclesWithDrivers({ includeLatestPlots: true, includePeripheralMappings: true })
            .then(vehicles => {
                this.vehicles = vehicles.filter(v => v.hasCamera);
            }).finally(() => {
                this.loading = false;
                this.createTableHeaders();
            })
    }

    vehicleClicked(vehicle: IVehicle) {
        this.alarmsLoading = true;
        this.selectedVehicle = vehicle;
        this.loadAlarms();
    }

    loadAlarms() {
        if (!this.selectedVehicle) {
            this.alarmsLoading = false;
            return;
        }
        this.tableRows = [];
        this.getAlarmsForVehicle().then(() => {
            this.alarmData.forEach(alarm => {

                this.createTableRows();

                this.searchFootage(alarm).then(() => {
                    this.createTableRows();
                })

                this.getAddressFromLatLng(alarm).then(() => {
                    this.createTableRows();
                });
            });

        }).finally(() => {
            this.alarmsLoading = false;
        });
    }

    onDateFilterChanged(filter: IDateFilter): void {
        this.alarmsLoading = true;
        this.dateFilter = filter;

        this.loadAlarms();
    }

    private getAlarmsForVehicle() {
        let alarmPromise = this.videoDiagnosticsService.getAlarms(this.selectedVehicle.Id, this.veriDate.createMoment(this.dateFilter.start), this.veriDate.createMoment(this.dateFilter.end)).then((alarms) => {
            this.alarmData = alarms;
        });
        return Promise.resolve(alarmPromise as Promise<void>);
    }

    private getAddressFromLatLng(alarm: any) {
        alarm.Location = "Loading...";
        let locationPromise = this.geocodeService
            .getAddressFromPoint(new google.maps.LatLng(alarm.GpsLat, alarm.GpsLng))
            .then(locationInformation => {
                alarm.Location = locationInformation.LocationDescription;
            });
        return Promise.resolve(locationPromise as Promise<void>);
    }

    private createTableHeaders() {
        this.tableHeaders = [
            this.veriTableService.createDateTimeHeader(this.translationService.translate("Common.Time"), { width: 160 }),
            { title: "Content (AlarmType)", sortable: false, width: 160 },
            { title: "Logged Status", sortable: false, width: 150 },
            { title: "Location", sortable: false, width: 180  },
            { title: "Events" }
        ];
    }
    private createTableRows() {
        this.tableRows = [];
        this.alarmData.forEach(alarm => {
            this.tableRows.push({
                data: alarm,
                cells: this.createTableCells(alarm)
            });
        });

    }

    private createTableCells(alarm: any): IVeriTableRowCell[] {

        let isIgnored = this.doesBackendIgnoreAlarm(alarm);

        let loggedStatus: string;
        if (alarm.NumberOfEvents === 'None' && !isIgnored) {
            loggedStatus = '<span style="color: red">MISSING</span>';
        }
        else if (isIgnored) {
            loggedStatus = '<span style="color: orange;">Ignored</span>';
        } else {
            loggedStatus = 'Logged';
        }

        const cells: IVeriTableRowCell[] = [
            this.veriTableService.createDateTimeCell(alarm.GpsTime, "fulldatetime"),
            { contents: `${alarm.Content} (${AlarmType[alarm.Type]})` },
            { contents: loggedStatus },
            { contents: alarm.Location },
            { contents: alarm.NumberOfEvents }
        ];

        return cells;
    }

    doesBackendIgnoreAlarm(alarm): boolean {
        let ignored = false;

        let sensorAlarmTypes: AlarmType[] = [AlarmType.IO1, AlarmType.IO2, AlarmType.IO3, AlarmType.IO4, AlarmType.IO5, AlarmType.IO6, AlarmType.IO7, AlarmType.IO8];

        this.ignoredCodes.forEach(code => {
            if (ignored) {
                return;
            }
            if (alarm.Content.toLowerCase().includes(code.toLowerCase()) || sensorAlarmTypes.includes(alarm.Type) && alarm.Content.toLowerCase().trim() === '') {
                ignored = true;
            }
            else {
                ignored = false;
            }
        })
        return ignored;
    }

    private searchFootage(alarm) {
        const startTime = this.dateService.addOrSubtractTime(new Date(alarm.Time), -30, DateUnit.Sec);
        const endTime = this.dateService.addOrSubtractTime(new Date(alarm.Time), 30, DateUnit.Sec);

        return this.videoEntryService
            .getVideosEntriesForVehicle(
                this.selectedVehicle.Id,
                startTime,
                endTime,
                { invalidateCache: true }).then((response) => {
                    let eventTypes: string[] = [];
                    response.forEach(res => {
                        eventTypes.push(res.displayEventCode);
                    })
                    let eventTypesString: string = eventTypes.join(', ')
                    alarm.NumberOfEvents = response.length ? `${response.length.toString()} event(s) available (${eventTypesString})` : 'None';
                })
    }

    streamVideo() {
        this.videoSourceUrl = this.tempVideoSourceUrl;
    }

    playbackVideo() {
        this.playbackSourceUrl = this.tempPlaybackSourceUrl;
    }
}

export class VideoDiagnosticsComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
        };

        this.controller = ComponentController;
        this.template = require("./videoDiagnostics.component.html");
    }
}
